import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as S from './styles';
import { BasicDialog } from '../../../../../components/newStudent/Dialog';
import TitleModal from '../../../../../components/newStudent/Title/TitleModal';
import QuillEditor from '../../../../../components/newStudent/InputTextQuill';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../components/newStudent/ProviderNewStudent';
import { ticketsServiceNewStudent } from '../../../../../services/newStudent';

export type TicketActionModalMode = 'action' | 'reopen';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  mode: TicketActionModalMode;
  protocol: string;
  onSuccess: () => void;
}

const modalContentByMode = {
  action: {
    title: 'Nova resposta',
    subtitle: 'Informe a descrição da nova ação no ticket.',
    submitLabel: 'Enviar resposta',
  },
  reopen: {
    title: 'Reabrir ticket',
    subtitle: 'Informe a justificativa para reabrir o ticket.',
    submitLabel: 'Reabrir ticket',
  },
};

export default function TicketActionModal({
  isOpen,
  setIsOpen,
  mode,
  protocol,
  onSuccess,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const content = modalContentByMode[mode];

  const handleClose = () => {
    setDescription('');
    setFiles([]);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const { result } =
        mode === 'action'
          ? await ticketsServiceNewStudent.newAction(
              { description },
              protocol,
              files,
            )
          : await ticketsServiceNewStudent.reopen(
              { description },
              protocol,
              files,
            );

      if (result) {
        handleClose();
        onSuccess();
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    setFiles(Array.from(event.target.files));
  };

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setFiles([]);
    }
  }, [isOpen, mode]);

  return (
    <BasicDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth="lg"
      fullWidth
      scroll="body"
      handleOnClose={handleClose}
    >
      <S.Container>
        <TitleModal
          title={content.title}
          subtitle={content.subtitle}
          setIsOpen={setIsOpen}
          handleOnClose={handleClose}
        />
        <S.FormContent>
          <S.EditorWrapper>
            <S.Label>Descrição</S.Label>
            <QuillEditor text={description} setText={setDescription} />
          </S.EditorWrapper>

          <S.AttachmentSection>
            <S.Label>Anexos</S.Label>
            <S.AttachmentRow>
              <ButtonSimple
                color="gray"
                outline
                widthSize="medium"
                onClick={() => fileInputRef.current?.click()}
              >
                Selecionar arquivos
              </ButtonSimple>
              <S.FileInput
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
              />
              {files.length > 0 && (
                <S.FileList>
                  {files.map((file) => (
                    <S.FileName key={`${file.name}-${file.size}`}>
                      {file.name}
                    </S.FileName>
                  ))}
                </S.FileList>
              )}
            </S.AttachmentRow>
          </S.AttachmentSection>

          <S.ButtonDiv>
            <ButtonSimple
              color="primary"
              widthSize="medium"
              onClick={handleSubmit}
            >
              {content.submitLabel}
            </ButtonSimple>
            <ButtonSimple
              color="gray"
              outline
              widthSize="medium"
              onClick={handleClose}
            >
              Cancelar
            </ButtonSimple>
          </S.ButtonDiv>
        </S.FormContent>
      </S.Container>
    </BasicDialog>
  );
}
